/**
 * ActionToolbar — Design A alternative to the radial ActionWheel.
 *
 * A small floating card that appears next to the clicked player, with a
 * 2-column grid of icon+label buttons. Bigger hit targets, real labels
 * (not just emojis), clean typography, sport-tinted accent.
 *
 * Drawing flow: click an action → cursor gets a sticky start point →
 * click on the court sets the endpoint → path auto-saves. No dragging.
 * This fixes the "cursor tracks without button press" bug in the radial
 * wheel where drawing would follow the cursor freely and never save.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';

const CARD_STYLE = {
  position: 'absolute',
  transform: 'translate(-50%, calc(-100% - 12px))',
  zIndex: 51,
  background: 'linear-gradient(180deg, rgba(20,24,35,0.98), rgba(9,10,15,0.98))',
  backdropFilter: 'blur(12px) saturate(180%)',
  WebkitBackdropFilter: 'blur(12px) saturate(180%)',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 10,
  padding: 8,
  boxShadow: '0 12px 32px rgba(0,0,0,0.5), 0 0 0 1px rgba(0,0,0,0.3)',
  minWidth: 220,
  maxWidth: 260,
  fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
};

const HEADER_STYLE = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 6px 8px 6px',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  marginBottom: 6,
};

const TITLE_STYLE = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: 0.4,
  textTransform: 'uppercase',
  color: 'rgba(255,255,255,0.55)',
};

const CLOSE_STYLE = {
  background: 'transparent',
  border: 'none',
  color: 'rgba(255,255,255,0.4)',
  fontSize: 14,
  cursor: 'pointer',
  padding: '2px 6px',
  borderRadius: 4,
  lineHeight: 1,
};

const GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 4,
};

const btnStyle = (active, hovered, accentColor) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  borderRadius: 7,
  border: '1px solid ' + (active ? accentColor : 'transparent'),
  background: active
    ? `${accentColor}22`
    : hovered
      ? 'rgba(255,255,255,0.06)'
      : 'transparent',
  color: active ? accentColor : 'rgba(255,255,255,0.92)',
  fontSize: 12,
  fontWeight: 500,
  cursor: 'pointer',
  textAlign: 'left',
  transition: 'background 0.12s ease, border-color 0.12s ease',
  fontFamily: 'inherit',
  minHeight: 34,
});

const iconStyle = {
  fontSize: 16,
  lineHeight: 1,
  flexShrink: 0,
  filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.4))',
};

const labelStyle = {
  flex: 1,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

const FOOTER_STYLE = {
  fontSize: 10,
  color: 'rgba(255,255,255,0.4)',
  padding: '6px 8px 2px 8px',
  borderTop: '1px solid rgba(255,255,255,0.06)',
  marginTop: 4,
  lineHeight: 1.4,
  minHeight: 26,
};

const ActionToolbar = ({ x, y, actions, onSelect, onDismiss, accentColor, sportId }) => {
  const cardRef = useRef(null);
  const [hoveredId, setHoveredId] = useState(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ left: `${x}%`, top: `${y}%` });

  const accent = accentColor || '#22d3ee';
  const displayActions = useMemo(() => actions.slice(0, 12), [actions]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  // Clamp card position so it doesn't overflow the surface container.
  useEffect(() => {
    if (!cardRef.current) return;
    const el = cardRef.current;
    const parent = el.offsetParent;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    const cardRect = el.getBoundingClientRect();
    let left = (x / 100) * parentRect.width;
    let top = (y / 100) * parentRect.height;

    // Card is translated -50%/-100% by CSS. Compute post-translate box.
    const boxLeft = left - cardRect.width / 2;
    const boxTop = top - cardRect.height - 12;
    const padding = 8;

    let adjLeft = left;
    let adjTop = top;
    let translate = 'translate(-50%, calc(-100% - 12px))';

    if (boxLeft < padding) adjLeft = padding + cardRect.width / 2;
    if (boxLeft + cardRect.width > parentRect.width - padding) {
      adjLeft = parentRect.width - padding - cardRect.width / 2;
    }
    if (boxTop < padding) {
      // Not enough room above — place below the player instead.
      translate = 'translate(-50%, 12px)';
    }

    setPos({ left: `${adjLeft}px`, top: `${adjTop}px`, transform: translate });
  }, [x, y, actions.length]);

  const hoveredAction = displayActions.find((a) => a.id === hoveredId);

  return (
    <div
      ref={cardRef}
      style={{
        ...CARD_STYLE,
        ...pos,
        opacity: visible ? 1 : 0,
        transition: 'opacity 0.14s ease-out, transform 0.14s ease-out',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={HEADER_STYLE}>
        <span style={TITLE_STYLE}>Choose action</span>
        <button
          type="button"
          style={CLOSE_STYLE}
          onClick={onDismiss}
          aria-label="Close"
        >×</button>
      </div>

      <div style={GRID_STYLE} role="menu">
        {displayActions.map((action) => {
          const isHovered = hoveredId === action.id;
          return (
            <button
              key={action.id}
              type="button"
              style={btnStyle(false, isHovered, accent)}
              onMouseEnter={() => setHoveredId(action.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => onSelect(action)}
              data-action-id={action.id}
              role="menuitem"
            >
              <span style={iconStyle} aria-hidden="true">{action.icon}</span>
              <span style={labelStyle}>{action.name || action.id}</span>
            </button>
          );
        })}
      </div>

      <div style={FOOTER_STYLE}>
        {hoveredAction
          ? (hoveredAction.description || `${hoveredAction.name} — click court to place endpoint`)
          : 'Hover an action for details · Esc to close'}
      </div>
    </div>
  );
};

export default ActionToolbar;
